import { CHANNEL_ACTIONS } from 'business/broadcast';
import { ExpGlobalDataDTO } from 'business/models/Models';
import { useBackdropLoading } from 'components/modal';
import connectionHubManager from 'connectionHub/connectionHubManager';
import RMHub from 'connectionHub/RMHub';
import IEventSubscription from 'generic/IEventSubscription';
import { useEffect, useRef } from 'react';
import { useRMDispatch } from 'redux/hooks';
import { useSetGlobalData, useSetIsFetchingGlobalData } from 'redux/hooks/common';
import { setSystemState, SystemState } from 'redux/reducers/commonReducer';
import { setGlobalDataLoaded } from 'redux/reducers/experimentReducer';
import { CacheHelper } from 'utils/CacheHelper';
import {
  clearUserDataAndLogout,
  GetScoreDefn,
  SetGlobalExpData,
  SetGlobalDataInMemory,
} from 'utils/Global';
import {
  getGlobalDataChannel,
  releaseTabLock,
  tryAcquireTabLock,
} from 'utils/GlobalDataTabLock';

const RECONNECTING_DELAY_MS = 1700;

const useFetchGlobalData = (token?: string | undefined) => {
  const dispatch = useRMDispatch();
  const setIsFetchingGlobalData = useSetIsFetchingGlobalData();
  const setGlobalData = useSetGlobalData();
  const { showBackdrop, hideBackdrop } = useBackdropLoading();
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  const loadExpGlobalData = async () => {
    if (!GetScoreDefn()) {
      setIsFetchingGlobalData(true);
      const gdata: ExpGlobalDataDTO = await SetGlobalExpData();
      if (gdata === null) {
        setIsFetchingGlobalData(false);
        return;
      }
      setGlobalData(gdata);
      dispatch(setGlobalDataLoaded(true));
      setIsFetchingGlobalData(false);
    }
  };

  useEffect(() => {
    let connection: RMHub | null;
    let hubEventHandle: IEventSubscription | null;
    let hubConnected: IEventSubscription | null;
    let hubReconnected: IEventSubscription | null;
    let hubReconnecting: IEventSubscription | null;
    let hubUnauthorized: IEventSubscription | null;
    let cacheEventHandle: IEventSubscription | null;
    broadcastChannelRef.current = getGlobalDataChannel();

    // listen for global data fetched by another tab
    broadcastChannelRef.current.onmessage = (event) => {
      if (event.data?.type === CHANNEL_ACTIONS.GLOBAL_DATA_UPDATE) {
        const gdata: ExpGlobalDataDTO = event.data.payload;
        SetGlobalDataInMemory(gdata);
        setGlobalData(gdata);
        dispatch(setGlobalDataLoaded(true));
        setIsFetchingGlobalData(false);
      }
    };

    const onStateChanged = (state: SystemState) => {
      dispatch(setSystemState(state));
    };
    if (!token) {
      showBackdrop('Initializing...');
    }

    try {
      loadExpGlobalData();
      const onReconnecting = () => {
        if (reconnectTimeoutRef.current) {
          return;
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          showBackdrop('Connection lost. Attempting to reconnect...');
        }, RECONNECTING_DELAY_MS);
      };
      const onReconnected = () => {
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        hideBackdrop();
      };
      const onUnauthorized = () => {
        hideBackdrop();
        clearUserDataAndLogout(true);
      };
      const initializeConnection = async () => {
        try {
          // Get or create connection
          connection = await connectionHubManager.getConnection(
            'notificationHub',
            '/notificationHub',
          );

          hubEventHandle = connection.attachHandlers([
            {
              eventName: 'GlobalDataUpdate',
              callback: async () => {
                if (!tryAcquireTabLock()) {
                  // another tab will fetch global data and broadcast it
                  return;
                }
                try {
                  const gdata: ExpGlobalDataDTO = await SetGlobalExpData(true);
                  broadcastChannelRef.current?.postMessage({
                    type: CHANNEL_ACTIONS.GLOBAL_DATA_UPDATE,
                    payload: gdata,
                  });
                  // set own gdata
                  setGlobalData(gdata);
                  dispatch(setGlobalDataLoaded(true));
                  setIsFetchingGlobalData(false);
                } finally {
                  releaseTabLock();
                }
              },
            },
            {
              eventName: 'StateChanged',
              callback: (state: SystemState) => onStateChanged(state),
            },
          ]);
          hubReconnecting = connection.reconnecting.add(onReconnecting);
          hubReconnected = connection.reconnected.add(onReconnected);
          hubConnected = connection.connected.add(onReconnected);
          hubUnauthorized = connection.unauthorized.add(onUnauthorized);
          const systemState = await connection.invoke<SystemState>('GetSystemState');
          dispatch(setSystemState(systemState));
          return connection;
        } catch (error) {
          console.error('Notification hub init failed:', error);
          showBackdrop('Failed to initialize connection');
        }
      };

      // Hub connection is started only when
      // token is already populated in cache
      cacheEventHandle = CacheHelper.oncacheadded('token', initializeConnection);

      CacheHelper.get('token').then(async (value) => {
        if (value) {
          await initializeConnection();
          cacheEventHandle?.dispose();
          cacheEventHandle = null;
        } else {
          hideBackdrop();
        }
      });
    } catch (e) {
      console.error('useFetchGlobalData error', e);
      hideBackdrop();
    }
    return () => {
      cacheEventHandle?.dispose();
      hubConnected?.dispose();
      hubReconnected?.dispose();
      hubReconnecting?.dispose();
      hubUnauthorized?.dispose();
      hubEventHandle?.dispose();
      broadcastChannelRef.current?.close();
      broadcastChannelRef.current = null;
      releaseTabLock();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // cleanup is performed when user logout
    };
  }, [token]);
};

export default useFetchGlobalData;
